import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNotifications } from '../contexts/NotificationContext';

/**
 * Custom hook to fetch pending gallery requests globally and add them to notifications
 * This ensures notifications appear immediately on admin login, even before visiting gallery page
 */
export const useGlobalGalleryNotifications = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const { addNotification, hasNotificationWithData } = useNotifications();

  // Check if we've already shown notifications for these requests (to prevent duplicates)
  const getProcessedRequests = useCallback(() => {
    try {
      const stored = localStorage.getItem('gallery_notifications_processed');
    } catch (error) {
      console.error('Error reading processed notifications:', error);
      return [];
    }
  }, []);

  // Store processed request IDs to prevent duplicates
  const markRequestAsProcessed = useCallback((requestId) => {
    try {
      const processed = getProcessedRequests();
      const updated = [...new Set([...processed, requestId])]; // Ensure uniqueness
      localStorage.setItem('gallery_notifications_processed', JSON.stringify(updated));
    } catch (error) {
      console.error('Error storing processed notification:', error);
    }
  }, [getProcessedRequests]);

  // Fetch pending gallery requests and add notifications
  const fetchPendingGalleryRequests = useCallback(async () => {
    if (isLoading || hasFetched) return;

    try {
      setIsLoading(true);
      const res = await axios.get(
  "https://aaapd.org.et/gallery_backend/api/getGalleryImages.php"
);


      if (Array.isArray(res.data)) {
        const pendingRequests = res.data.filter(img => img.status === "pending");

        // Filter out requests that already have notifications
        const newRequests = pendingRequests.filter(request =>
          !hasNotificationWithData('imageId', request.id)
        );

        if (newRequests.length > 0) {
          newRequests.forEach((request) => {
            addNotification({
              type: 'gallery',
              title: 'Pending Gallery Request',
              message: `${request.author || 'Someone'} submitted "${request.alt_text || 'an image'}" for approval`,
              data: {
                imageId: request.id,
                author: request.author,
                altText: request.alt_text,
                description: request.description,
                src: request.src
              }
            });

            // Mark this request as processed to prevent future duplicates
            markRequestAsProcessed(request.id);
          });
        }

        setHasFetched(true);
      }
    } catch (error) {
      console.error('Error fetching gallery requests for notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, hasFetched, addNotification, getProcessedRequests, markRequestAsProcessed]);

  // Reset fetch state when needed (e.g., for re-fetching)
  const resetFetchState = useCallback(() => {
    setHasFetched(false);
  }, []);

  return {
    fetchPendingGalleryRequests,
    resetFetchState,
    isLoading,
    hasFetched
  };
};
